import { Controller } from "@hotwired/stimulus";
import {
  encodeWav,
  floatTo16BitPcm,
  TARGET_SAMPLE_RATE,
  UtteranceMeta,
} from "../lib/speech/audio";
import {
  SpeechCapture,
  SpeechCaptureState,
} from "../lib/speech/capture";
import { pushSpeechLevel, speechLevelTrace } from "../lib/speech/level_trace";
import {
  UploadContext,
  UploadQueue,
  UploadQueueCapacityError,
  UploadQueueState,
} from "../lib/speech/upload_queue";

type DictationState =
  | "idle"
  | "requesting"
  | "listening"
  | "transcribing"
  | "ready"
  | "failed";

type FailureKind =
  | "start"
  | "capture"
  | "upload"
  | "capacity"
  | "finish"
  | "poll"
  | "processing"
  | "discard";

interface TranscriptionLinks {
  self: string;
  chunks: string;
  finish: string;
  discard: string;
}

interface TranscriptionChunkPayload {
  sequence: number;
  status: "uploaded" | "transcribing" | "ready" | "failed";
  transcript_text?: string | null;
  error_code?: string | null;
}

interface TranscriptionPayload {
  id: number;
  status: "recording" | "processing" | "ready" | "failed" | "abandoned";
  next_sequence?: number;
  error_code?: string | null;
  error_message?: string | null;
  chunks?: TranscriptionChunkPayload[];
  links?: TranscriptionLinks;
}

interface CapturedUtterance {
  audio: Blob;
  startedAtMs: number;
  endedAtMs: number;
}

const REQUEST_TIMEOUT_MS = 30_000;
const MAX_POLL_INTERVAL_MS = 5_000;

/**
 * Generic browser transport for a server-backed speech transcription.
 *
 * Controls and text insertion belong to consumer adapters. This controller
 * translates their speech-dictation:* commands into microphone capture and
 * HTTP requests, then emits speech-transcription:* state and segment events.
 */
export default class extends Controller<HTMLFormElement> {
  static targets = ["pushedSegment"];

  static values = {
    startUrl: String,
    // Poll only while an uploaded chunk is unresolved. A quarter second keeps
    // dictation feeling immediate without turning idle recording into traffic;
    // failures still back off to MAX_POLL_INTERVAL_MS.
    pollInterval: { type: Number, default: 250 },
  };

  declare startUrlValue: string;
  declare hasStartUrlValue: boolean;
  declare pollIntervalValue: number;

  private capture: SpeechCapture | null = null;
  private uploadQueue: UploadQueue<CapturedUtterance> | null = null;
  private links: TranscriptionLinks | null = null;
  private transcriptionId: number | null = null;
  private transcriptionStatus: TranscriptionPayload["status"] | null = null;
  private uploadedSequences = new Set<number>();
  private deliveredSequences = new Set<number>();
  private failureKind: FailureKind | null = null;
  private stopRequested = false;
  private connected = false;
  private suppressCaptureState = false;
  private generation = 0;
  private lastState: DictationState | null = null;
  private pollTimer: number | null = null;
  private pollFailures = 0;
  private pendingPolls = 0;
  private pollInFlight = false;
  private pollRequested = false;
  private mutationRevision = 0;
  private responseEpoch = 0;
  private requestControllers = new Set<AbortController>();

  connect() {
    this.connected = true;
  }

  pushedSegmentTargetConnected(element: HTMLElement) {
    const transcriptionId = Number(element.dataset.transcriptionId);
    const sequence = Number(element.dataset.sequence);
    const text = element.textContent || "";
    element.remove();

    if (!this.connected || transcriptionId !== this.transcriptionId) return;
    if (!Number.isInteger(sequence) || sequence < 0) return;

    this.deliverSegment(sequence, text);
    if (!this.pollingNeeded()) this.cancelPoll();
  }

  disconnect() {
    this.connected = false;
    this.generation += 1;
    const discardUrl =
      this.transcriptionStatus && !["ready", "abandoned"].includes(this.transcriptionStatus)
        ? this.links?.discard
        : null;

    this.releaseLocalResources();
    this.abortRequests();
    if (discardUrl) this.bestEffortDiscard(discardUrl);
  }

  async start() {
    if (!this.hasStartUrlValue || this.transcriptionStatus === "recording") return;

    const generation = ++this.generation;
    this.releaseLocalResources();
    this.resetSessionState();
    this.emitState("requesting");

    try {
      const mutationRevision = ++this.mutationRevision;
      const payload = await this.requestJson(this.startUrlValue, {
        method: "POST",
        body: new FormData(),
      });
      if (!this.current(generation) || !payload || mutationRevision !== this.mutationRevision) return;

      this.adoptPayload(payload, true);
      this.responseEpoch += 1;
      if (this.stopRequested) {
        await this.discardEmptySession(generation);
        return;
      }
      this.buildUploadQueue(payload.next_sequence || 0);
      this.buildCapture();
      await this.capture!.start();
    } catch {
      if (this.current(generation)) this.fail("start");
    }
  }

  async stop() {
    const generation = this.generation;
    this.stopRequested = true;
    this.emitState("transcribing");
    if (!this.links) return;
    if (!this.capture || !this.uploadQueue) {
      await this.discardEmptySession(generation);
      return;
    }

    this.capture.stop({ flush: true });
    speechLevelTrace.clear();
    await this.uploadQueue.drain();
    if (!this.current(generation)) return;

    if (this.uploadQueue.hasFailures) {
      this.fail("upload");
      return;
    }

    if (this.uploadedSequences.size === 0) {
      await this.discardEmptySession(generation);
      return;
    }

    await this.finishSession(generation);
  }

  async discard() {
    const discardUrl = this.links?.discard;
    const generation = ++this.generation;
    this.releaseLocalResources();
    this.abortRequests();

    if (discardUrl) {
      try {
        await this.requestJson(discardUrl, { method: "DELETE" });
      } catch {
        if (this.connected && generation === this.generation) this.fail("discard");
        return;
      }
    }

    if (!this.connected || generation !== this.generation) return;
    this.resetSessionState();
    this.emitState("idle");
  }

  private buildCapture() {
    this.capture = new SpeechCapture({
      profile: "dictation",
      onUtterance: (samples, meta) => this.enqueueUtterance(samples, meta),
      // The room's level, for whatever is drawing it (the speech-wave
      // controller beside the composer). Shared with the survey's
      // conversation dock: one microphone, one trace.
      onLevel: (level) => pushSpeechLevel(level),
      onStateChange: (state) => this.captureStateChanged(state),
    });
  }

  private buildUploadQueue(startSequence: number) {
    this.uploadQueue = new UploadQueue<CapturedUtterance>({
      startSequence,
      upload: (utterance, context) => this.uploadChunk(utterance, context),
      onStateChange: (state) => this.uploadStateChanged(state),
    });
  }

  private captureStateChanged(state: SpeechCaptureState) {
    if (!this.connected || this.suppressCaptureState) return;

    switch (state) {
      case "requesting":
        this.emitState("requesting");
        break;
      case "listening":
        this.failureKind = null;
        this.emitState("listening");
        break;
      case "permission-denied":
      case "unsupported":
      case "failed":
        this.fail("capture");
        break;
      case "idle":
        if (!this.stopRequested) this.emitState("idle");
        break;
      case "paused":
        break;
    }
  }

  private uploadStateChanged(state: UploadQueueState) {
    if (this.failureKind === "capacity") return;
    if (state === "failed") this.fail("upload");
    if (state === "capacity-exceeded") this.fail("capacity");
  }

  private enqueueUtterance(samples: Float32Array, meta: UtteranceMeta) {
    if (!this.uploadQueue) return;

    const audio = encodeWav(floatTo16BitPcm(samples));
    const startedAtMs = Math.max(0, Math.round(meta.startMs));
    // meta.endMs deliberately means "last voiced frame," while the emitted
    // WAV includes its closing silence. The API validates the stated duration
    // against the WAV, so derive persistence timing from the actual samples.
    const durationMs = Math.max(1, Math.round((samples.length / TARGET_SAMPLE_RATE) * 1_000));
    const utterance = { audio, startedAtMs, endedAtMs: startedAtMs + durationMs };

    try {
      this.uploadQueue.enqueue(utterance, { bytes: audio.size });
    } catch (error) {
      if (!(error instanceof UploadQueueCapacityError)) throw error;
      this.fail("capacity");
    }
  }

  private async uploadChunk(utterance: CapturedUtterance, context: UploadContext) {
    if (!this.links) return false;

    const generation = this.generation;
    const links = this.links;
    const formData = new FormData();
    formData.append("sequence", String(context.sequence));
    formData.append("started_at_ms", String(utterance.startedAtMs));
    formData.append("ended_at_ms", String(utterance.endedAtMs));
    formData.append("audio", utterance.audio, `speech_chunk_${context.sequence}.wav`);
    this.cancelPoll();
    const mutationRevision = ++this.mutationRevision;
    const payload = await this.requestJson(
      links.chunks,
      { method: "POST", body: formData },
      context.signal
    );
    if (!this.current(generation) || this.links !== links) return true;

    this.uploadedSequences.add(context.sequence);
    if (!payload || mutationRevision !== this.mutationRevision) {
      if (this.pollingNeeded()) this.schedulePoll(this.pollIntervalValue, true);
      return true;
    }
    this.processPayload(payload);
    this.responseEpoch += 1;
    this.pendingPolls = 0;
    if (this.shouldPoll(payload)) this.schedulePoll(this.pollIntervalValue, true);
    else this.cancelPoll();
    return true;
  }

  private async finishSession(generation: number) {
    if (!this.links || !this.current(generation)) return;

    this.failureKind = null;
    this.emitState("transcribing");
    try {
      this.cancelPoll();
      const mutationRevision = ++this.mutationRevision;
      const payload = await this.requestJson(this.links.finish, {
        method: "POST",
        body: new FormData(),
      });
      if (!this.current(generation) || !payload || mutationRevision !== this.mutationRevision) return;
      this.processPayload(payload);
      this.responseEpoch += 1;
      this.pendingPolls = 0;
      if (this.shouldPoll(payload)) this.schedulePoll(this.pollIntervalValue, true);
      else this.cancelPoll();
    } catch {
      if (this.current(generation)) this.fail("finish");
    }
  }


  private async discardEmptySession(generation: number) {
    if (!this.links || !this.current(generation)) return;

    try {
      await this.requestJson(this.links.discard, { method: "DELETE" });
      if (!this.current(generation)) return;
      this.releaseLocalResources();
      this.resetSessionState();
      this.emitState("idle");
    } catch {
      if (this.current(generation)) this.fail("discard");
    }
  }

  private schedulePoll(delay = this.pollIntervalValue, replace = false) {
    if (!this.connected || !this.links) return;
    if (this.pollTimer !== null) {
      if (!replace) return;
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.pollTimer = window.setTimeout(() => {
      this.pollTimer = null;
      void this.poll();
    }, delay);
  }

  private async poll() {
    if (!this.links) return;
    if (this.pollInFlight) {
      this.pollRequested = true;
      return;
    }

    this.pollInFlight = true;
    const generation = this.generation;
    const responseEpoch = this.responseEpoch;
    try {
      const payload = await this.requestJson(this.links.self);
      if (!this.current(generation) || !payload || responseEpoch !== this.responseEpoch) return;
      const deliveredBefore = this.deliveredSequences.size;
      this.processPayload(payload);
      if (this.shouldPoll(payload)) {
        this.pendingPolls = this.deliveredSequences.size > deliveredBefore ? 0 : this.pendingPolls + 1;
        const delay = Math.min(this.pollIntervalValue * 2 ** this.pendingPolls, MAX_POLL_INTERVAL_MS);
        this.schedulePoll(delay);
      } else {
        this.pendingPolls = 0;
      }
    } catch {
      if (!this.current(generation) || responseEpoch !== this.responseEpoch) return;
      this.pollFailures += 1;
      if (this.pollFailures >= 3) {
        this.fail("poll");
        return;
      }
      const delay = Math.min(
        this.pollIntervalValue * 2 ** this.pollFailures,
        MAX_POLL_INTERVAL_MS
      );
      this.schedulePoll(delay);
    } finally {
      this.pollInFlight = false;
      if (this.pollRequested && this.current(generation) && this.pollingNeeded()) {
        this.pollRequested = false;
        this.schedulePoll(this.pollIntervalValue, true);
      } else {
        this.pollRequested = false;
      }
    }
  }

  private processPayload(payload: TranscriptionPayload) {
    this.pollFailures = 0;
    this.adoptPayload(payload);
    for (const chunk of payload.chunks || []) {
      if (chunk.status !== "ready") continue;

      this.deliverSegment(chunk.sequence, chunk.transcript_text || "");
    }

    if (payload.status === "ready") {
      this.failureKind = null;
      this.emitState("ready");
    } else if (payload.status === "failed") {
      this.fail("processing");
    } else if (payload.status === "abandoned") {
      this.emitState("idle");
    }
  }

  private deliverSegment(sequence: number, text: string) {
    if (this.deliveredSequences.has(sequence)) return;

    this.deliveredSequences.add(sequence);
    this.dispatch("segment", { detail: { sequence, text } });
  }

  private shouldPoll(payload: TranscriptionPayload) {
    if (["ready", "failed", "abandoned"].includes(payload.status)) return false;
    if (payload.status === "processing") return true;
    return [...this.uploadedSequences].some(
      (sequence) => !this.deliveredSequences.has(sequence)
    );
  }

  private pollingNeeded() {
    if (!this.transcriptionStatus || ["ready", "failed", "abandoned"].includes(this.transcriptionStatus)) return false;
    if (this.transcriptionStatus === "processing") return true;
    return [...this.uploadedSequences].some((sequence) => !this.deliveredSequences.has(sequence));
  }

  private adoptPayload(payload: TranscriptionPayload, linksRequired = false) {
    this.transcriptionId = payload.id;
    if (payload.links) this.links = payload.links;
    if (linksRequired && !this.validLinks(this.links)) {
      throw new Error("Speech transcription response did not include valid links");
    }
    this.transcriptionStatus = payload.status;
  }

  private validLinks(links: TranscriptionLinks | null): links is TranscriptionLinks {
    return Boolean(
      links?.self && links.chunks && links.finish && links.discard
    );
  }

  private fail(kind: FailureKind) {
    this.failureKind = kind;
    if (["upload", "capacity", "poll"].includes(kind) && !this.stopRequested) {
      // Whatever was being said when the failure hit is still an utterance:
      // flush it into the queue, which keeps trying to deliver what it holds.
      // Only a queue that is already full has nowhere to put it.
      this.capture?.pause({ flush: kind !== "capacity" });
    }
    this.emitState("failed", true);
  }

  private emitState(state: DictationState, force = false) {
    if (!force && this.lastState === state) return;
    this.lastState = state;
    this.dispatch("state", { detail: { state } });
  }

  private resetSessionState() {
    this.links = null;
    this.transcriptionId = null;
    this.transcriptionStatus = null;
    this.uploadedSequences.clear();
    this.deliveredSequences.clear();
    this.failureKind = null;
    this.stopRequested = false;
    this.pollFailures = 0;
    this.pendingPolls = 0;
    this.pollInFlight = false;
    this.pollRequested = false;
    this.mutationRevision = 0;
    this.responseEpoch = 0;
    this.lastState = null;
  }

  private releaseLocalResources() {
    this.cancelPoll();
    this.suppressCaptureState = true;
    this.capture?.stop({ flush: false });
    this.suppressCaptureState = false;
    this.capture = null;
    speechLevelTrace.clear();
    this.uploadQueue?.dispose();
    this.uploadQueue = null;
  }

  private cancelPoll() {
    if (this.pollTimer !== null) window.clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  private abortRequests() {
    this.requestControllers.forEach((controller) => controller.abort());
    this.requestControllers.clear();
  }

  private current(generation: number) {
    return this.connected && generation === this.generation;
  }

  private async requestJson(
    url: string,
    init: RequestInit = {},
    externalSignal?: AbortSignal
  ): Promise<TranscriptionPayload | null> {
    const controller = externalSignal ? null : new AbortController();
    const signal = externalSignal || controller!.signal;
    if (controller) this.requestControllers.add(controller);
    const timeout = window.setTimeout(() => controller?.abort(), REQUEST_TIMEOUT_MS);
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (this.csrfToken) headers.set("X-CSRF-Token", this.csrfToken);

    try {
      const response = await fetch(url, {
        ...init,
        headers,
        signal,
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(`Speech transcription request failed (${response.status})`);
      if (response.status === 204) return null;
      return (await response.json()) as TranscriptionPayload;
    } finally {
      window.clearTimeout(timeout);
      if (controller) this.requestControllers.delete(controller);
    }
  }

  private bestEffortDiscard(url: string) {
    const headers = new Headers({ Accept: "application/json" });
    if (this.csrfToken) headers.set("X-CSRF-Token", this.csrfToken);
    void fetch(url, {
      method: "DELETE",
      headers,
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => undefined);
  }

  private get csrfToken() {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
  }
}
